import { useCallback, useEffect, useRef } from "react";
import { Easing, Group, Tween } from "@tweenjs/tween.js";

import { CARD_W, CARD_H, PERSPECTIVE, layoutTargets } from "./forgeWikiLayouts";
import { useAppearance } from "../../appearance";

/**
 * The card stage, with nothing in it that knows how a card is drawn.
 * ---------------------------------------------------------------------------
 * This is the animation half of three.js's `css3d_periodictable` - its tweens,
 * its `transform()`, its scattered load - plus everything the product has
 * grown around it since: a camera that turns about the card under the hand,
 * reading one card close up, fitting the arrangement to the stage, walking the
 * cards with the keyboard, the shelf, the tour, follow mode.
 *
 * None of that has anything to do with WHAT a card looks like, and until now
 * all of it lived in the stage that writes CSS transforms onto DOM elements.
 * So when the same scene had to be drawn by the GPU instead, the choice was to
 * write the camera a second time or to take it out of the renderer. It is out:
 * every number here is worked out in the same coordinates either way, and a
 * SURFACE is handed in to do the one thing the two cannot share.
 *
 * A surface is three functions:
 *
 *   paint(objects, camera, blend)  put the scene on the screen, however it
 *                                  does that - one transform string per card,
 *                                  or a draw call
 *   count()                        how many cards it is actually holding
 *   cardAt(event)                  which card the pointer is over, or -1
 *   guard(event)                   true if this event belongs to the page's
 *                                  own furniture - a document being scrolled,
 *                                  a list being clicked - and is not the
 *                                  stage's to take
 *
 * ...and, optionally, the camera itself:
 *
 *   ownsCamera                     true when the surface has a camera of its
 *                                  own and this file is to keep its hands off
 *                                  it: no pointer handlers, no tween onto it
 *                                  when the arrangement changes, no tour
 *   fit(ms) / focus(i, o)          what to do instead when the stage asks for
 *   autoplay(on)                   those - the surface's own versions
 *
 * A surface that owns its camera still gets everything else: the cards are
 * laid out, tweened and painted exactly as before. What it takes over is
 * where the camera stands, which is the one thing a renderer with a real
 * camera can do better than a model written for CSS transforms.
 *
 * Everything else - where the cards go, where the camera stands, what the
 * wheel does, what a click does - is here, once, for both.
 *
 * COORDINATES, which is what makes one camera serve two renderers
 * ----------------------------------------------------------------
 * The scene is described the way CSS describes it, because that is what it was
 * built in and the numbers are all in pixels:
 *
 *   world  = translateZ(P - distance) · translate(ox, oy)
 *            · rotateX(pitch) · rotateY(yaw) · translate(-target)
 *   card   = translate(o.x, o.y, o.z) · rotateY(ry) · rotateX(rx) · rotateZ(rz)
 *   zoom   = a flat scale outside the perspective
 *
 * with `P` the focal length (PERSPECTIVE), so a point that lands at camera
 * depth z is drawn at P/(distance - z) of its size. A renderer with a real
 * camera reproduces that exactly - the lens is `fov = 2·atan(H / 2P)`, the eye
 * stands `distance` back along the camera's own axis, and the flat scale is
 * the camera's zoom - which is why both surfaces agree on where every card is
 * to the pixel, and why the focused card can be a DOM element sitting on top
 * of a canvas without either of them drifting.
 * ---------------------------------------------------------------------------
 *
 * @param stageRef  the viewport, which clips and which the pointer is on
 * @param surface   see above
 * @param enabled   false while the other surface is drawing the scene, in
 *                  which case this one binds nothing at all - the pointer
 *                  handlers live on the same element, and a stage that is not
 *                  drawing must not be taking the hand's events either
 * @param layout    "table" | "sphere" | "grid"
 * @param count     how many cards are in the world right now
 * @param shelf     the chosen card, which puts the cards in a column at the
 *                  top-left corner with that one at the head of it; -1 for the
 *                  arrangement itself
 * @param autoplay  while true the camera turns and tilts on its own
 * @param follow    while true every card looks at the middle of the screen
 *                  rather than the way its arrangement put it
 * @param onInteract called when the pointer takes the camera back, so the page
 *                  can drop out of autoplay rather than fight the hand on it
 * @param onShelfStep called with -1 or 1 when the wheel is turned while the
 *                  cards are stacked: there the wheel walks the stack, the way
 *                  the arrow keys do, rather than zooming
 * @returns { resetCamera, fitToScreen, focusCard, bringIntoView, objects,
 *            camera, targets, project }
 */

/** The example's own 2000ms, a little quicker: the cards are bigger. */
const DURATION = 1400;

/** The box a card is brought up to fill when it is clicked. */
const FOCUS_BOX = 400;

/** The gap left above it, once it is up there. Kept in step with the GPU
    surface's FLY_TOP_PAD, which is the same measurement in the other
    renderer. */
const FOCUS_TOP_PAD = 24;

/* How much bigger the chosen card is drawn than the rest.
   -------------------------------------------------------------------------
   And the rest are left ALONE. Shrinking the neighbours to make room for it
   was the obvious way to give it space and the wrong one: a shelf where the
   cards around the one you are looking at quietly change size is a shelf that
   is answering a different question every time the selection moves. The card
   grows where it stands, over its neighbours rather than into a gap they made
   for it, and everything else on the stage is exactly where and what it was. */
const CHOSEN_SIZE = 1.4;

/** How much wheel turn one card of the stack costs. */
const STACK_STEP = 70;

const TAU = Math.PI * 2;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const mix = (a, b, t) => a + (b - a) * t;

/**
 * The same angle, expressed as the one nearest where the card is now.
 *
 * Two cards on opposite sides of the sphere differ by nearly a full turn in
 * yaw, and going from 3.1 to -3.1 takes the long way round - the card spins
 * through the whole circle to arrive a few degrees from where it started.
 */
function nearestAngle(to, from) {
  return to + TAU * Math.round((from - to) / TAU);
}

function prefersReducedMotion() {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (e) {
    return false;
  }
}

export function useForgeStageCore(stageRef, {
  surface, enabled = true, layout, count, shelf = -1,
  autoplay = false, follow = false, selected = -1, onInteract, onShelfStep,
}) {
  /* The surface is swapped when the renderer is, and the handlers below are
     bound once - so they reach it through a ref rather than closing over
     whichever one was current when the stage mounted. */
  const skin = useRef(surface);
  skin.current = surface;
  /* Whether the camera below is this file's to move, or the surface's. */
  const owned = () => !!skin.current?.ownsCamera;
  /* How far in the stage will go, which is somebody's setting rather than a
     constant: Appearance > Max zoom. Kept in a ref as well, because the wheel's
     handler is bound once and would otherwise go on clamping to whatever the
     setting was when the page opened. */
  const [maxZoom] = useAppearance("zoom");
  const ceiling = useRef(maxZoom);
  ceiling.current = maxZoom;

  // One object per card - position and rotation in the world - which is what
  // the tweens actually animate. The DOM is written from them, never read.
  const objects = useRef([]);
  // The camera: how far back it stands and where it is looking. Its distance
  // is the arrangement's business (see layoutTargets), its angles the
  // pointer's.
  const camera = useRef({
    /* Where the camera stands. Set once per arrangement (cameraDistance in
       forgeWikiLayouts works out where a photographer would stand for it), and
       never touched again: a turn moves the pivot across the camera's plane
       rather than along its axis, and a zoom is a flat scale outside the
       perspective. Neither may change how strong the perspective reads. */
    distance: PERSPECTIVE * 2, yaw: 0, pitch: 0,
    // the magnification, applied outside the perspective so type stays sharp
    zoom: 1,
    // what the camera turns about - the origin, until a card is focused or a
    // drag starts on one
    tx: 0, ty: 0, tz: 0,
    // and where that point sits on screen. Zero puts it in the middle, which is
    // what focusing wants; a drag that starts on a card sets it so the card
    // stays exactly where it already is while the scene turns around it.
    ox: 0, oy: 0,
  });
  // which card is being read close up, so clicking it again lets it go
  const focused = useRef(-1);
  /* The chosen card, read by the frame rather than by an effect: it decides
     how big every card is drawn and the answer changes as the selection
     walks. */
  const chosen = useRef(selected);
  chosen.current = selected;
  /* ...and whether the sizes are still settling, which is a reason to keep
     painting even though no tween is running. */
  const settling = useRef(false);
  // 0 = the cards face the way the arrangement put them, 1 = they face the
  // middle of the screen. Tweened between the two, never jumped.
  const followBlend = useRef(follow ? 1 : 0);
  /* Read by the wheel handler, which is set up once and must not be rebuilt
     every time the stack is re-headed. */
  const shelfRef = useRef(shelf);
  const shelfStepRef = useRef(onShelfStep);
  shelfRef.current = shelf;
  shelfStepRef.current = onShelfStep;
  // Where the arrangement says each card belongs, kept apart from where each
  // card currently IS: a fit tweens every card home from wherever it stands, and
  // this is what they are put back to afterwards.
  const targets = useRef([]);
  const group = useRef(new Group());
  const frame = useRef(0);
  const running = useRef(false);
  // Set while the pointer is dragging, so the loop keeps painting even when no
  // tween is running.
  const dirty = useRef(false);
  const seeded = useRef(false);

  /**
   * One frame.
   *
   * The only thing worked out here is WHICH WAY each card faces, because that
   * is a property of the scene rather than of the renderer: left alone a card
   * faces the way its arrangement put it - out of the sphere, as the periodic
   * table's own tiles do, which is why half of them show their backs - and
   * follow mode turns it to the middle of the screen instead.
   *
   * The world is turned by `rotateX(pitch) rotateY(yaw)`, so a card that
   * undoes exactly that comes out square to the screen; and because it is
   * worked out here, per frame, rather than tweened into the card, it keeps
   * facing us while the scene is being turned.
   *
   * `blend` is what makes the switch a movement rather than a jump: it is
   * tweened between the two on toggle, and every card takes the angle that far
   * between where its arrangement put it and where the screen is.
   *
   * Then the surface is handed the lot and paints it.
   */
  const render = useCallback(() => {
    const cam = camera.current;
    const blend = followBlend.current;
    const bill = blend > 0.0005;

    const cards = objects.current;

    /* How big each card is drawn.
       -------------------------------------------------------------------
       One card is bigger than the rest and nothing else changes at all - it
       does not move, its neighbours do not move and they do not shrink. The
       arrangement somebody is looking at is the one they chose; the chosen
       card simply takes more of the room where it already stands, and is
       drawn over the ones it now overlaps.

       Eased towards rather than jumped to, per frame, which is also why the
       loop is told it is still settling - a selection that walks with the
       arrow keys would otherwise snap through four sizes a second. */
    const at = chosen.current;
    const pick = at >= 0 ? cards[at] : null;
    let moving = false;

    for (let i = 0; i < cards.length; i++) {
      const o = cards[i];
      if (!o) continue;

      if (bill) {
        o.fx = mix(o.rx, -cam.pitch, blend);
        o.fy = mix(o.ry, nearestAngle(-cam.yaw, o.ry), blend);
        o.fz = mix(o.rz, 0, blend);
      } else {
        o.fx = o.rx;
        o.fy = o.ry;
        o.fz = o.rz;
      }

      const want = pick && i === at ? CHOSEN_SIZE : 1;

      if (o.k === undefined) o.k = want;
      else if (Math.abs(want - o.k) > 0.002) {
        o.k += (want - o.k) * 0.22;
        moving = true;
      } else o.k = want;
    }

    settling.current = moving;
    skin.current?.paint(cards, cam, blend);
  }, []);

  /* The example's animate(): update the tweens, paint, and keep going while
     anything is still moving. It stops when nothing is - a still scene costs
     no frames. */
  const tick = useCallback(() => {
    group.current.update(performance.now());
    render();

    if (group.current.getAll().length > 0 || dirty.current || settling.current) {
      frame.current = requestAnimationFrame(tick);
    } else {
      running.current = false;
    }
  }, [render]);

  const start = useCallback(() => {
    if (running.current) return;
    running.current = true;
    frame.current = requestAnimationFrame(tick);
  }, [tick]);

  /**
   * The example's `transform(targets, duration)`: every card gets a tween to
   * its new place and another to its new facing, and the camera pulls back far
   * enough to hold whatever shape they are making.
   */
  const transform = useCallback((duration = DURATION) => {
    const stage = stageRef.current;
    if (!stage) return;

    /* How many cards there ARE is the surface's answer, not this file's: the
       DOM one counts the elements it was given, which is zero while the stack
       is up and the cards are a list instead. */
    const n = skin.current?.count() || 0;
    if (!n) return;

    const { targets: places, camera: placed } = layoutTargets(layout, n, {
      width: stage.clientWidth || 1200,
      height: stage.clientHeight || 700,
      shelf,
    });
    targets.current = places;

    const ms = prefersReducedMotion() ? 0 : duration;

    // A card that has just appeared starts life scattered, like the example's
    // elements do at load, so it flies in rather than fading up in place.
    for (let i = 0; i < n; i++) {
      if (!objects.current[i]) {
        objects.current[i] = {
          x: Math.random() * 4000 - 2000,
          y: Math.random() * 4000 - 2000,
          z: Math.random() * 4000 - 2000,
          rx: 0, ry: 0, rz: 0,
        };
      }
    }
    objects.current.length = n;

    group.current.removeAll();

    /* Stacked, every card faces front and they all move together: one tween
       each, no ragged arrival, no rotation to animate. That is half the tweens
       of an arrangement change, and it is what lets a scroll through the stack
       keep pace with the wheel. */
    const stacked = shelfRef.current >= 0;
    const ease = stacked ? Easing.Quadratic.Out : Easing.Exponential.InOut;

    for (let i = 0; i < n; i++) {
      const o = objects.current[i];
      const t = places[i];

      new Tween(o, group.current)
        .to({ x: t.x, y: t.y, z: t.z }, stacked ? ms : ms + Math.random() * ms)
        .easing(ease)
        .start();

      if (stacked) {
        o.rx = t.rx;
        o.ry = t.ry;
        o.rz = t.rz;
        continue;
      }

      new Tween(o, group.current)
        .to({
          rx: nearestAngle(t.rx, o.rx),
          ry: nearestAngle(t.ry, o.ry),
          rz: nearestAngle(t.rz, o.rz),
        }, ms + Math.random() * ms)
        .easing(Easing.Exponential.InOut)
        .start();
    }

    // The camera travels with them: each arrangement has its own size, and the
    // distance is what keeps all of it inside the viewport. An arrangement is
    // also a fresh view of everything, so the magnification goes back to life
    // size and whatever card was being read closely is let go.
    focused.current = -1;
    if (!owned()) {
      new Tween(camera.current, group.current)
        /* Both the arrangements and the shelf bring a camera with them: the
           magnification that frames them and where the lens is pointed. Never
           a distance - that one never moves. */
        .to(placed, ms)
        .easing(Easing.Exponential.InOut)
        .start();
    }

    // The example's third tween - the one that animates nothing and exists
    // only to keep rendering for as long as the slowest of the others. Ours is
    // the loop's own stop condition, so it is the same idea said once.
    if (ms === 0) {
      group.current.update(performance.now() + 1);
      render();
      return;
    }
    start();
  }, [layout, shelf, stageRef, render, start]);

  /* How the effects below reach the two things defined further down - putting
     a reading back, and fitting the stage. Effects cannot name them directly:
     they are declared below, so a dependency array mentioning them would be
     read before they exist. */
  const refocus = useRef(null);
  const refit = useRef(null);

  /* ── Every arrangement is fitted ────────────────────────────────────────
     An arrangement brings its own camera distance with it (cameraDistance in
     forgeWikiLayouts: where a photographer would stand for a shape of this
     size). That is solved for the SHAPE, and it knows nothing about this
     stage - the column beside it, the band above it, a window somebody made
     narrow - so what arrived was a shelf sitting in three quarters of the room
     it had. The first thing to do on arriving was press Space, which solves
     the distance against the cards as they actually stand.

     So that is done for you, after the cards land: on arrival, on a change of
     arrangement, and after the stage changes size. Space is then a way back to
     this view rather than a correction to it.

     Never while a card is being read, though - that camera is somebody's. */
  const fitSoon = useCallback((after) => {
    /* Soon, not after: the fit is measured against where the cards are going,
       so it does not have to wait for them to get there - it only has to be
       after the targets are set, which is the tick the arrangement starts on.
       The camera then travels with the cards rather than correcting them once
       they have landed. */
    const at = setTimeout(() => { if (focused.current < 0) refit.current?.(Math.min(after, 700)); }, 60);
    return () => clearTimeout(at);
  }, []);

  /* Arrangement, or the number of cards in it, changed. */
  useEffect(() => {
    if (!enabled || !(skin.current?.count())) return;
    // The first pass is the example's load: scattered cards flying into the
    // table. After that a change of arrangement is a change of arrangement.
    // A shelf re-stacks quickly: it is a step through a list, not a change of
    // arrangement, and at full length the column would still be sliding when
    // the next key arrives.
    // and at 520 every step restarted a half-second tween on every card, which
    // is what made scrolling the stack feel heavy
    const ms = shelf >= 0 ? 260 : (seeded.current ? DURATION : DURATION * 1.4);
    transform(ms);
    seeded.current = true;
    /* ...and then framed against the stage it landed on */
    return fitSoon(ms);
  }, [enabled, layout, count, shelf, transform, fitSoon]);

  /* The stage resized: the table's column count and every arrangement's camera
     distance are measured against it, so both are re-solved - quickly, because
     this is a correction rather than a move. */
  useEffect(() => {
    const stage = stageRef.current;
    if (!enabled || !stage || typeof ResizeObserver === "undefined") return undefined;

    let first = true;
    const ro = new ResizeObserver(() => {
      if (first) { first = false; return; }
      /* The arrangement is measured against the stage, so a stage that changed
         size has to be re-solved. What must NOT change is which card is being
         read: on the applications page, clicking a card with findings opens the
         column beside the stage, which narrows the stage, which lands here -
         and the re-solve let the card go and pulled the camera back, so a click
         appeared to do nothing at all. The reading is taken back afterwards,
         against the places the cards are now going to. */
      const reading = focused.current;
      transform(400);
      if (reading >= 0) refocus.current?.(reading); else fitSoon(400);
    });
    ro.observe(stage);
    return () => ro.disconnect();
    // `count` is in here because the stage itself only exists once there are
    // cards to put on it: a ref does not change identity when its element
    // mounts, so without this the observer would be set up against nothing and
    // never retried.
  }, [stageRef, transform, count, enabled, fitSoon]);

  /* The camera's controls, in place of TrackballControls: drag to turn the
     scene, wheel to stand closer or further back.

     A drag that starts ON a card turns the scene about THAT card: the pointer
     is on it, so it is what the movement should be measured around. The card
     does not move to the middle to do it - the pivot keeps whatever place on
     screen the card already had (`ox`/`oy` above), so nothing jumps when the
     drag begins and the arrangement swings around the card under the hand.

     A card's own controls still work, because a press is not a drag until the
     pointer has actually travelled: under four pixels it is a click and the
     card handles it, over four it becomes a turn and the click that would
     follow is swallowed. */
  useEffect(() => {
    const stage = stageRef.current;
    /* A surface with its own camera has its own controls on this element, and
       two sets of handlers fighting over one drag is one set too many. */
    if (!enabled || !stage || owned()) return undefined;

    let pointerId = null;
    let lastX = 0;
    let lastY = 0;
    let downX = 0;
    let downY = 0;
    let pending = null;    // the card the press landed on, until it is a drag
    let turning = false;
    let travelled = false;
    let wheelAcc = 0;      // how much turn is banked towards the next card
    let wheelAt = 0;
    let wheelPending = 0;

    /* The list and the reading panel are ordinary page furniture: they scroll,
       their links and controls are clicked, and their text is selected. Both
       sit inside the stage, so every one of those events passes these handlers
       first - and they have to let them through, or the document can be read
       but never used and the list never scrolled. */
    const inDoc = (e) => !!skin.current?.guard?.(e);

    /* Make this card the point the scene turns about, without moving it: the
       pivot becomes its position, and the screen offset is set to wherever the
       card is being drawn right now. */
    const pivotOn = (index) => {
      const o = objects.current[index];
      if (!o) return;
      const cam = camera.current;

      const cy = Math.cos(cam.yaw);
      const sy = Math.sin(cam.yaw);
      const cp = Math.cos(cam.pitch);
      const sp = Math.sin(cam.pitch);

      const px = o.x - cam.tx;
      const py = o.y - cam.ty;
      const pz = o.z - cam.tz;

      const x = px * cy + pz * sy;
      const z1 = -px * sy + pz * cy;
      const y = py * cp - z1 * sp;
      /* the card's own depth is deliberately not worked out: see below */

      /* Moving the pivot without moving anything on screen - and without
         moving the camera.
         -----------------------------------------------------------------
         The card is at (x, y, z) in camera space. The pivot goes to the point
         on its line of sight at the depth the pivot already has: across the
         camera's own plane by (x, y), and not a step along its axis. Every
         card then shifts by exactly -(x, y, 0) in camera space, which the
         screen offset undoes, so the frame at the moment the drag starts is
         the frame it continues from.

         Taking the card's z as well would put the pivot at the card itself,
         but only by walking the camera forward to compensate - and that is
         the one thing that changes how strong the perspective reads. The
         difference in where the scene turns is a few degrees; the difference
         in how it looks was every card on the stage. */
      const cy2 = Math.cos(-cam.yaw);
      const sy2 = Math.sin(-cam.yaw);
      const cp2 = Math.cos(-cam.pitch);
      const sp2 = Math.sin(-cam.pitch);

      // the camera-space step (x, y, 0), put back into world axes:
      // R⁻¹ = Ry(-yaw) · Rx(-pitch)
      const wy = y * cp2;
      const wz1 = y * sp2;
      const wx = x * cy2 + wz1 * sy2;
      const wz = -x * sy2 + wz1 * cy2;

      cam.ox += x;
      cam.oy += y;
      cam.tx += wx;
      cam.ty += wy;
      cam.tz += wz;
    };

    const beginTurn = (e) => {
      turning = true;
      travelled = true;
      if (pending !== null) { pivotOn(pending); pending = null; }
      dirty.current = true;
      stage.classList.add("is-dragging");
      /* Capture is taken HERE and not on the press, because a captured pointer
         sends its click to the element that holds the capture: taking it on
         every press meant the stage received the click and the card under it
         never heard about it, so a plain click stopped taking cards up.

         It can refuse - a pointer that has already been released, which is
         what a synthesised event is - and a refusal must not be the end of
         the turn: the turn works without capture, it just stops tracking if
         the pointer leaves the stage. */
      try { stage.setPointerCapture?.(e.pointerId); } catch (err) { /* no such pointer */ }
      start();
    };

    const onPointerDown = (e) => {
      if (e.button !== 0) return;
      if (inDoc(e)) return;
      /* -1 for "not on a card", and -2 for "on a card's own controls", which
         are theirs alone - a press on the document button is not a press on
         the card and must not become a turn either. */
      const card = skin.current?.cardAt(e) ?? -1;
      if (card === -2) return;

      // a hand on the scene ends the tour
      if (autoOn.current) onInteract?.();

      pointerId = e.pointerId;
      lastX = downX = e.clientX;
      lastY = downY = e.clientY;
      travelled = false;

      if (card >= 0) {
        // not a turn yet: it becomes one only if the pointer travels
        pending = card;
        turning = false;
      } else {
        pending = null;
        beginTurn(e);
      }
    };

    const onPointerMove = (e) => {
      if (pointerId !== e.pointerId) return;
      if (inDoc(e)) return;

      if (!turning) {
        if (Math.hypot(e.clientX - downX, e.clientY - downY) < 4) return;
        beginTurn(e);
      }

      const cam = camera.current;
      cam.yaw += (e.clientX - lastX) * 0.005;
      // Stopping short of straight up or down: past the pole the scene turns
      // inside out, which reads as the drag having broken something.
      cam.pitch = clamp(cam.pitch - (e.clientY - lastY) * 0.005, -1.2, 1.2);
      lastX = e.clientX;
      lastY = e.clientY;
    };

    const onPointerUp = (e) => {
      if (pointerId !== e.pointerId) return;
      pointerId = null;
      pending = null;
      turning = false;
      dirty.current = false;
      stage.classList.remove("is-dragging");
      if (stage.hasPointerCapture?.(e.pointerId)) stage.releasePointerCapture(e.pointerId);
    };

    /* The click that follows a drag is not a click on anything: a pointer that
       travelled across a card was turning the scene, and letting that through
       would take the card up as well. */
    const onClickCapture = (e) => {
      if (!travelled) return;
      travelled = false;
      e.stopPropagation();
      e.preventDefault();
    };

    const onWheel = (e) => {
      // the document scrolls itself
      if (inDoc(e)) return;
      e.preventDefault();
      const cam = camera.current;
      // A wheel notch, a trackpad flick and a mouse that reports in lines are
      // three different numbers for the same gesture, and one big event must
      // not teleport the camera - so the step is normalised and capped.
      if (autoOn.current) onInteract?.();

      /* Stacked, the wheel walks the stack.
         -------------------------------------------------------------------
         A column of cards is a list, and a list answers to a scroll - so in
         that one mode the wheel steps between them instead of zooming, exactly
         as the arrow keys do. The turn is accumulated and spent a card at a
         time, because a trackpad flick is thirty small events and would
         otherwise run the whole shelf past in one go. */
      if (shelfRef.current >= 0) {
        const now = performance.now();
        if (now - wheelAt > 400) wheelAcc = 0;
        wheelAt = now;
        wheelAcc += e.deltaY * (e.deltaMode === 1 ? 16 : 1);

        /* One card per frame at most. A trackpad sends thirty events for one
           flick, and stepping on each of them asked the stage for thirty
           re-stacks in the time it could draw two - which is what the lag was.
           The turn is banked, one step is taken on the next frame in whichever
           direction the bank leans, and the bank itself is capped so a long
           flick does not keep stepping after the fingers have stopped. */
        if (!wheelPending) {
          wheelPending = requestAnimationFrame(() => {
            wheelPending = 0;
            if (Math.abs(wheelAcc) < STACK_STEP) return;
            const dir = wheelAcc > 0 ? 1 : -1;
            wheelAcc = clamp(wheelAcc - dir * STACK_STEP, -STACK_STEP * 3, STACK_STEP * 3);
            shelfStepRef.current?.(dir);
          });
        }
        return;
      }

      /* Zooming towards the card under the pointer, rather than into the middle
         of the stage.

         It is the same pivot the drag uses: the card the wheel is over becomes
         the point the scene is seen about (jump-free, see pivotOn), and then the
         zoom simply has to leave that point where it is. Since the pivot is
         drawn at `ox · PERSPECTIVE / distance · zoom`, holding it still while
         the zoom changes by f means dividing the offset by f - and the rest of
         the scene grows outwards from it, which is what "towards the card"
         means. With nothing under the pointer the pivot is the middle of the
         stage and this is the zoom it always was. */
      const i = skin.current?.cardAt(e) ?? -1;
      if (i >= 0) {
        const o = objects.current[i];
        // already turning about this card's line of sight? then leave it alone
        if (o && (Math.abs(o.x - cam.tx) > 1 || Math.abs(o.y - cam.ty) > 1)) pivotOn(i);
      }

      const lines = e.deltaMode === 1 ? 16 : 1;
      const step = clamp(e.deltaY * lines, -160, 160);
      // The zoom, not the camera's distance: see .forge-wiki-stage__zoom. The
      // sign flips because zooming IN is scrolling up, which is a negative
      // delta, and a bigger scale.
      const was = cam.zoom;
      cam.zoom = clamp(cam.zoom * (1 - step * 0.0008), 0.15, ceiling.current);

      const f = cam.zoom / was;
      if (f !== 1) {
        cam.ox /= f;
        cam.oy /= f;
      }

      dirty.current = true;
      start();
      // one frame past the wheel, then the loop is free to stop again
      requestAnimationFrame(() => { dirty.current = false; });
    };

    stage.addEventListener("click", onClickCapture, true);
    stage.addEventListener("pointerdown", onPointerDown);
    stage.addEventListener("pointermove", onPointerMove);
    stage.addEventListener("pointerup", onPointerUp);
    stage.addEventListener("pointercancel", onPointerUp);
    stage.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      stage.removeEventListener("click", onClickCapture, true);
      stage.removeEventListener("pointerdown", onPointerDown);
      stage.removeEventListener("pointermove", onPointerMove);
      stage.removeEventListener("pointerup", onPointerUp);
      stage.removeEventListener("pointercancel", onPointerUp);
      stage.removeEventListener("wheel", onWheel);
    };
    // `count` for the same reason as the observer above - the stage mounts with
    // the first card, after this effect would otherwise have run once against
    // a ref that was still empty.
  }, [stageRef, start, count, enabled, onInteract]);

  /* Nothing may keep painting after the page is gone. */
  useEffect(() => {
    const tweens = group.current;
    return () => {
      cancelAnimationFrame(frame.current);
      running.current = false;
      tweens.removeAll();
    };
  }, []);

  /* A card was chosen, or let go: the sizes have somewhere new to travel to. */
  useEffect(() => { start(); }, [selected, start]);

  /* Turning follow on and off: the blend travels between the arrangement's own
     facing and the screen's, and the loop paints while it does. */
  useEffect(() => {
    const to = follow ? 1 : 0;
    if (followBlend.current === to) return;
    const ms = prefersReducedMotion() ? 0 : 520;
    new Tween(followBlend, group.current)
      .to({ current: to }, ms)
      .easing(Easing.Quadratic.InOut)
      .start();
    if (ms === 0) group.current.update(performance.now() + 1);
    start();
  }, [follow, start]);

  /**
   * Autoplay: the camera turning and tilting on its own.
   *
   * One tween at a time, each to a new angle picked at random and over a random
   * few seconds, chained end to end - so the movement never repeats and never
   * arrives anywhere in particular. It is the same Tween the rest of the stage
   * uses, so a layout change or a click interrupts it as it would anything
   * else, and the chain simply picks up from wherever the camera ended up.
   */
  const autoOn = useRef(false);
  const autoTimer = useRef(0);

  const autoStep = useCallback(() => {
    if (!autoOn.current) return;
    const cam = camera.current;
    const ms = 3400 + Math.random() * 3000;

    new Tween(cam, group.current)
      .to({
        // a turn of between a third and a full half circle, either way round
        yaw: cam.yaw + (0.9 + Math.random() * 2.2) * (Math.random() < 0.5 ? -1 : 1),
        // and a tilt that stays well short of the poles
        pitch: (Math.random() * 1.5 - 0.75),
      }, ms)
      .easing(Easing.Quadratic.InOut)
      .start();

    start();
    autoTimer.current = setTimeout(autoStep, ms);
  }, [start]);

  useEffect(() => {
    autoOn.current = autoplay;
    if (owned()) {
      /* the surface turns its own camera - see useForgeGpuStage */
      skin.current?.autoplay?.(autoplay);
      return undefined;
    }
    if (autoplay) {
      autoStep();
    } else {
      clearTimeout(autoTimer.current);
    }
    return () => clearTimeout(autoTimer.current);
  }, [autoplay, autoStep]);

  /**
   * Where a point in the world lands on the screen, in pixels from the middle
   * of the stage, and how big it is drawn there.
   *
   * The same arithmetic the fit does, and the same the renderers do: the
   * camera's target first, then rotateY, then rotateX - the order the world's
   * transform list applies them in - and then the perspective divide. One
   * place, so nothing can disagree about where a card is.
   */
  const project = useCallback((o) => {
    const cam = camera.current;
    if (!o) return null;

    const cy = Math.cos(cam.yaw);
    const sy = Math.sin(cam.yaw);
    const cp = Math.cos(cam.pitch);
    const sp = Math.sin(cam.pitch);

    const px = o.x - cam.tx;
    const py = o.y - cam.ty;
    const pz = o.z - cam.tz;

    const x = px * cy + pz * sy;
    const z1 = -px * sy + pz * cy;
    const y = py * cp - z1 * sp;
    const z = py * sp + z1 * cp;

    const s = (PERSPECTIVE / Math.max(80, cam.distance - z)) * cam.zoom;
    return { x: (x + cam.ox) * s, y: (y + cam.oy) * s, s, depth: cam.distance - z };
  }, []);

  /**
   * Put every card back where the arrangement says it belongs - what undoes a
   * parting when the card being read is let go.
   */
  const restoreTargets = useCallback((ms) => {
    targets.current.forEach((t, i) => {
      const o = objects.current[i];
      if (!o) return;
      new Tween(o, group.current)
        .to({ x: t.x, y: t.y, z: t.z }, ms)
        .easing(Easing.Exponential.InOut)
        .start();
    });
  }, []);

  /* The cards standing in front of the one being read used to be swept aside,
     one left, the next right, like a curtain opening - and put back when it was
     let go. That is gone: a click moves the camera and nothing else. Reading one
     card is not a reason for the shelf to rearrange itself, and the arrangement
     someone is looking at is the one they chose. */

  /** Back to the arrangement's own camera, square on. */
  const resetCamera = useCallback(() => {
    camera.current.yaw = 0;
    camera.current.pitch = 0;
    transform(600);
  }, [transform]);

  /**
   * Stand at the distance that holds everything currently on the stage, from
   * where the camera is already looking.
   *
   * The arrangement's own distance (layoutTargets) is solved for a camera
   * square on to it. Once the scene has been turned, that number is wrong in
   * both directions - a sphere seen from above needs less room than one seen
   * edge on, a grid seen at an angle needs more - so this one is solved
   * against the cards as they actually stand, through the rotation the camera
   * actually has. The angles are not touched: what was being looked at is
   * still being looked at, from far enough away to see all of it.
   *
   * Each card is taken as its centre plus the radius of its own corner, since
   * a card in the sphere is turned and its footprint on screen is
   * somewhere between its width and its diagonal.
   *
   * ...and taken where it is GOING, not where it is. Pressed by hand the two
   * are the same - the cards are at rest by then. Asked for the moment an
   * arrangement starts, they are not: the cards are still flying in, spread
   * wider than the shape they are about to make, and a fit measured against
   * that cloud frames a shape that stops existing a second later. The targets
   * are the shape; the positions are the journey.
   */
  const fitToScreen = useCallback((duration = 600) => {
    const stage = stageRef.current;
    if (!stage || !objects.current.length) return;
    const places = targets.current.length === objects.current.length
      ? targets.current
      : objects.current;

    const W = stage.clientWidth || 1200;
    const H = stage.clientHeight || 700;
    const cam = camera.current;
    const d = cam.distance;

    const cy = Math.cos(cam.yaw);
    const sy = Math.sin(cam.yaw);
    const cp = Math.cos(cam.pitch);
    const sp = Math.sin(cam.pitch);
    const R = Math.hypot(CARD_W, CARD_H) / 2;

    // How far out the arrangement reaches on screen at life size, from where
    // the camera is standing. The fit is then simply the magnification that
    // brings that reach back inside the viewport - which is also what makes
    // this work in both directions, in and out.
    let reachX = 1;
    let reachY = 1;
    places.forEach(o => {
      // The world's own transform, done to a point: the camera's target first,
      // then rotateY, then rotateX - the order the world's transform list
      // applies them in.
      const px = o.x - cam.tx;
      const py = o.y - cam.ty;
      const pz = o.z - cam.tz;

      const x = px * cy + pz * sy;
      const z1 = -px * sy + pz * cy;
      const y = py * cp - z1 * sp;
      const z = py * sp + z1 * cp;

      // a point at depth (d - z) is drawn at PERSPECTIVE/(d - z) of its size
      const s = PERSPECTIVE / Math.max(80, d - z);
      reachX = Math.max(reachX, (Math.abs(x) + R) * s);
      reachY = Math.max(reachY, (Math.abs(y) + R) * s);
    });

    /* 0.99, not 0.96: the fit is asked for when someone wants to SEE the
       arrangement, and four per cent of the stage left empty all the way round
       is a visible band of nothing. A hair of margin is kept so the outermost
       cards' edges are not flush against the panel. */
    const zoom = clamp(Math.min((W / 2) / reachX, (H / 2) / reachY) * 0.99, 0.15, ceiling.current);

    const ms = prefersReducedMotion() ? 0 : duration;
    focused.current = -1;
    group.current.removeAll();
    new Tween(camera.current, group.current)
      .to({ zoom, tx: 0, ty: 0, tz: 0, ox: 0, oy: 0 }, ms)
      .easing(Easing.Exponential.InOut)
      .start();
    // fitting is a view of the whole arrangement, so any card that stepped
    // aside for a focused one takes its place again
    restoreTargets(ms);

    if (ms === 0) {
      group.current.update(performance.now() + 1);
      render();
      return;
    }
    start();
  }, [stageRef, render, start, restoreTargets]);

  /**
   * Bring a card into view, if it is not already.
   * ---------------------------------------------------------------------------
   * Walking the arrangement with the arrows moves the SELECTION, not the
   * camera, so on anything bigger than a screenful the walk eventually left the
   * stage: the card being read was half an inch past the edge, or not on it at
   * all. There is nothing to scroll here - the shelf is a camera pointed at a
   * scene - so the camera is what moves, and only when it has to.
   *
   * "Has to" is the card's own box against the stage's, with a margin so a card
   * flush against the edge still counts as out: a card you can see the edge of
   * is not a card you are reading. When it must move it goes the whole way and
   * centres the card, which is one movement rather than a nudge that leaves the
   * next step needing another.
   *
   * The magnification is not touched. This is the shelf being walked, not a
   * card being read - the zoom belongs to whoever set it.
   * ------------------------------------------------------------------------- */
  const bringIntoView = useCallback((index, { margin = 28, duration = 420 } = {}) => {
    const stage = stageRef.current;
    const o = targets.current[index] || objects.current[index];
    if (!stage || !o) return false;

    /* Where it lands, worked out rather than measured.
       ---------------------------------------------------------------------
       This used to ask the card's DOM element for its bounding box, which was
       the obvious thing to do when every card WAS one. It also asked at the
       wrong moment - the box is where the card is now, halfway through its
       tween, rather than where it is going - and there is no element to ask
       when the GPU is drawing. The projection is three lines and it answers
       for both surfaces, about the arrangement rather than the journey. */
    const half = project(o);
    if (!half) return false;

    const W = (stage.clientWidth || 1200) / 2;
    const H = (stage.clientHeight || 700) / 2;
    const reach = (Math.hypot(CARD_W, CARD_H) / 2) * half.s;

    const inside = Math.abs(half.x) + reach <= W - margin
      && Math.abs(half.y) + reach <= H - margin;
    if (inside) return false;

    const ms = prefersReducedMotion() ? 0 : duration;
    group.current.removeAll();
    new Tween(camera.current, group.current)
      .to({ tx: o.x, ty: o.y, tz: o.z, ox: 0, oy: 0 }, ms)
      .easing(Easing.Exponential.InOut)
      .start();
    /* the cards carry on to wherever they were going: removing the tweens above
       took theirs with the camera's */
    restoreTargets(ms);

    if (ms === 0) {
      group.current.update(performance.now() + 1);
      render();
    } else {
      start();
    }
    return true;
  }, [stageRef, render, start, restoreTargets, project]);

  /**
   * Read one card closely: bring it to the middle of the stage and magnify it
   * to fill a 400x400 box.
   *
   * Centring is the camera's target: the world is translated so that the card's
   * own position becomes the origin, which is where the camera points. Facing
   * it needs nothing at all - every card is already square to the screen.
   *
   * The magnification then follows from the box: a card standing at the
   * camera's target is at depth `distance`, so it is drawn at
   * PERSPECTIVE/distance of its size, and the zoom is whatever multiplies that
   * up to the box's 400px on the card's longer side.
   *
   * Calling it on the card already being read lets it go again, back to the
   * arrangement's own framing - unless `toggle` says otherwise, which is what
   * the search wants: typing another letter that lands on the same card should
   * keep it up, not put it down.
   *
   * @returns true if a card was taken up, false if one was let go
   */
  const focusCard = useCallback((index, { duration = 700, toggle = true } = {}) => {
    /* Where the card will BE, not where it is. The two differ only while a
       re-layout is in flight - and that is exactly when this is called a second
       time, to put a reading back after the stage changed size. Aiming at the
       card's current position then would point the camera at a card halfway
       through its own tween, and it would drift out of the middle as it
       landed. */
    const o = targets.current[index] || objects.current[index];
    if (!o) return false;

    const ms = prefersReducedMotion() ? 0 : duration;
    const settle = () => {
      if (ms === 0) {
        group.current.update(performance.now() + 1);
        render();
      } else {
        start();
      }
    };

    if (toggle && focused.current === index) {
      focused.current = -1;
      group.current.removeAll();
      new Tween(camera.current, group.current)
        .to({ zoom: 1, tx: 0, ty: 0, tz: 0, ox: 0, oy: 0, yaw: 0, pitch: 0 }, ms)
        .easing(Easing.Exponential.InOut)
        .start();
      // the cards that stepped aside come back
      restoreTargets(ms);
      settle();
      return false;
    }

    const cam = camera.current;
    const zoom = clamp(
      (FOCUS_BOX / Math.max(CARD_W, CARD_H)) * (cam.distance / PERSPECTIVE),
      /* reading a card close up is the same magnification by another name, so
         it answers to the same ceiling */
      0.15, ceiling.current,
    );

    // Where the camera points, how close it stands - and, when the cards are
    // facing the way their arrangement put them, the angles that bring this one
    // square to the lens: a card's own facing is `rotateY(ry) rotateX(rx)` and
    // the world is turned by `rotateX(pitch) rotateY(yaw)`, so the two cancel
    // at `yaw = -ry, pitch = -rx`. In follow mode the card already faces us and
    // the scene is left at whatever angle it is being read from.
    /* ...and held at the top of the stage rather than the middle of it, for
       the reason the GPU surface holds it there (see FLY_TOP_PAD): what is
       underneath the card is what the page is for. The offset is in the
       world's own pixels, so it is divided back out of the two things that
       stand between it and the screen - the lens at this distance, and the
       zoom on the box outside it. */
    /* ...and the chosen card is drawn CHOSEN_SIZE bigger than the box, which
       is 35px of its own height. Measuring the box and not the card is how it
       ends up with its top edge off the stage. */
    const cardH = (CARD_H * FOCUS_BOX * CHOSEN_SIZE) / Math.max(CARD_W, CARD_H);
    const h = stageRef.current?.clientHeight || 700;
    const lift = Math.max(0, h / 2 - cardH / 2 - FOCUS_TOP_PAD);
    const to = {
      tx: o.x, ty: o.y, tz: o.z,
      ox: 0, oy: -lift * (cam.distance / (PERSPECTIVE * zoom)),
      zoom,
    };
    if (followBlend.current < 0.5) {
      to.yaw = nearestAngle(-o.ry, cam.yaw);
      to.pitch = nearestAngle(-o.rx, cam.pitch);
    }

    focused.current = index;
    group.current.removeAll();
    new Tween(camera.current, group.current)
      .to(to, ms)
      .easing(Easing.Exponential.InOut)
      .start();

    /* ...and every card stays exactly where the arrangement put it: the only
       thing that moves is the camera. Home first all the same, in case a fit or
       an older focus left anything short of its own place. */
    restoreTargets(ms);

    settle();
    return true;
  }, [render, start, restoreTargets, stageRef]);

  /* ...and the observer can reach it now that it exists. */
  useEffect(() => {
    refocus.current = (index) => (owned()
      ? skin.current?.focus?.(index, { duration: 400, toggle: false })
      : focusCard(index, { duration: 400, toggle: false }));
  }, [focusCard]);

  useEffect(() => {
    refit.current = (ms) => (owned()
      ? skin.current?.fit?.(ms ?? 500)
      : fitToScreen(ms ?? 500));
  }, [fitToScreen]);

  return {
    resetCamera, fitToScreen, focusCard, bringIntoView, project,
    /* the scene itself, for a surface that needs to read it outside a paint -
       a raycast against where the cards actually are, say */
    objects, camera, targets,
    CARD_W, CARD_H,
  };
}

export default useForgeStageCore;
